/**
 * THE TABLE DOCK — the multi-tabling cluster's shared geometry and its names.
 *
 * The dock is one family with three members, named here because they live in
 * different files for historical reasons:
 *
 *   AddTableSlot   — the dashed "+" (the `addSlot` cells inside YourTables)
 *   TableTiles     — the fan-card tiles (YourTables rendered `bare`)
 *   TableCountPicker — the segmented how-many-in-view control
 *
 * They always appear together, in that order, joined by the grey dock divider,
 * and they share the geometry below.
 *
 * The two tiers of the table header's top row.
 *
 * The row had three sizes and three corner radii — 32px/r5 for the hamburger
 * and the add-table slot, 40px/r8 for support, ~58px/r6 for the fan tiles — so
 * nothing lined up with anything (Jason 2026-08-17). Sizing them all identically
 * is the wrong correction though: these are two different kinds of control and
 * flattening them would say they are one.
 *
 *   TABLE-SHAPED (58 / r6) — the fan tiles and the add-table slot. Both stand
 *   for a table: one holds a hand, the other is the empty place a new one goes.
 *   They are the tallest things in the band because they carry real content.
 *
 *   CONTROLS (40 / r8) — the hamburger, support, and the table-count selector.
 *   Things you press to change something, all the same size, deliberately
 *   smaller so they never compete with the tables for attention.
 *
 * The selector was briefly given the 58px table height on the theory that
 * everything in the multi-tabling group should match. That was the wrong line
 * to draw (Jason 2026-08-17): its content is four 16px icons, so at table height
 * it read as an oversized empty box, while the add-table slot carries 58px
 * comfortably because it is card-shaped by definition. POSITION says the
 * selector belongs to the tables — it sits in the cluster, at the cluster gap,
 * right of the fans. SIZE says it is a control. Both are true.
 *
 * Everything in the row imports from here rather than restating a number, so
 * "make them consistent" is a property of the code and not of somebody
 * remembering.
 */

/** Height of the table-shaped members — the fan tiles and the add-table slot. */
export const TABLE_TILE_HEIGHT = 58;

/**
 * Height of the count selector.
 *
 * It sits at TABLE height, not CONTROL height. On the grid page 40px matched
 * Lobby / support / the balance pill, but on the single-table view none of those
 * exist — so a 40px selector was an orphan size in a row that already had three
 * (Jason 2026-08-17). Matching the tiles it stands beside beats matching
 * controls that are far away or absent: proximity is what the eye groups by.
 * One height in both views, so the two can never disagree.
 */
export const SELECTOR_HEIGHT = TABLE_TILE_HEIGHT;

/**
 * Corner radius across the cluster — the fan tile's existing `.previewFrame`
 * radius, which the other members now match rather than the other way round:
 * the tile is the biggest surface and the one carrying real content, so it wins.
 */
export const CLUSTER_RADIUS = 6;

/**
 * Height shared by everything you PRESS: the hamburger, support, and the
 * table-count selector. The first two are square at this size; the selector is
 * this tall and as wide as its segments need.
 */
export const CONTROL_HEIGHT = 40;

/** Corner radius across the controls. */
export const CONTROL_RADIUS = 8;

// The cluster's horizontal rhythm is NOT defined here — it is TILE_GAP in
// YourTables, because it is a property of the fan row's own slot geometry and
// the header just follows it. Keeping one owner is what stops the "+" and the
// selector drifting to different distances from the tiles.

/**
 * ONE DOCK, TWO SURFACES (EN-10545 — Jason 8/21): the table↔grid switch is a
 * double-buffered cut, so if the dock renders at identical pixels on both
 * surfaces it reads as a single persistent dock that the cut never touches.
 * The members already share their sizes (above) and their rhythm (TILE_GAP),
 * and both surfaces center the cluster on the viewport — measured on the
 * Storybook rig, every member's box and center-offset matches to the pixel
 * once the single-view overlay drops its legacy 1.25 up-scale. What can still
 * split them is the BAND: YourTables fits the row to its container
 * (scale = min(1, band / natural)), so two different container caps mean two
 * different fitted sizes at tight widths. Both surfaces therefore cap their
 * band with THIS clearance — viewport minus room for the widest side chrome
 * either surface carries (grid bar: logo+support left, balance+avatar right;
 * single overlay: the scaled corner clusters and the felt's right action
 * rail). Same cap ⇒ same fit ⇒ same dock, at every width.
 */
export const DOCK_BAND_EDGE_CLEARANCE_PX = 210;

/** The shared band cap both surfaces apply to the dock's container. */
export const DOCK_BAND_MAX_WIDTH = `calc(100vw - ${2 * DOCK_BAND_EDGE_CLEARANCE_PX}px)`;
